import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Filter, 
  X, 
  Plus,
  FileText,
  List,
  CheckCircle,
  Calculator,
  BookOpen,
  Star
} from 'lucide-react';
import { clsx } from 'clsx';
import { useQuestionLibraryStore } from '../../stores/questionLibraryStore';
import QuestionLibraryCard from './QuestionLibraryCard';

const QuestionLibrarySidebar = ({ 
  isOpen, 
  onClose, 
  onInsertQuestion,
  context, // 'document' or 'assessment'
  contextInfo // document or assessment info
}) => {
  const {
    questions,
    isLoading,
    filters,
    loadQuestions,
    loadCategories,
    setFilters
  } = useQuestionLibraryStore();

  const [showFilters, setShowFilters] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      loadQuestions();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      loadQuestions();
    }
  }, [filters]);

  const handleSearch = (searchTerm) => {
    setFilters({ searchTerm });
  };

  const categories = [
    { id: 'all', label: 'All Questions', icon: BookOpen },
    { id: 'math', label: 'Math Questions', icon: Calculator },
    { id: 'multiple-choice', label: 'Multiple Choice', icon: List },
    { id: 'text', label: 'Text Questions', icon: FileText },
    { id: 'true-false', label: 'True/False', icon: CheckCircle },
    { id: 'popular', label: 'Popular', icon: Star }
  ];

  const getFilteredQuestions = () => {
    if (selectedCategory === 'all') return questions;
    if (selectedCategory === 'popular') {
      return [...questions].sort((a, b) => (b.views || 0) - (a.views || 0));
    }
    return questions.filter(question => question.type === selectedCategory);
  };

  const filteredQuestions = getFilteredQuestions();

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div className={clsx(
        'fixed top-0 right-0 h-full w-96 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50',
        'flex flex-col',
        isOpen ? 'translate-x-0' : 'translate-x-full'
      )}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-sigma-50">
          <div className="flex items-center space-x-2">
            <BookOpen className="w-5 h-5 text-sigma-600" />
            <h2 className="text-lg font-semibold text-gray-900">Question Library</h2>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={clsx(
                'p-2 rounded-lg transition-colors',
                showFilters 
                  ? 'bg-sigma-100 text-sigma-700' 
                  : 'text-gray-500 hover:bg-gray-100'
              )}
            >
              <Filter className="w-4 h-4" />
            </button>
            
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Context Info */}
        {contextInfo && (
          <div className="p-4 bg-blue-50 border-b border-blue-200">
            <div className="text-sm">
              <div className="font-medium text-blue-900">
                Adding to {context}:
              </div>
              <div className="text-blue-700 truncate">
                {contextInfo.title || contextInfo.name || 'Untitled'}
              </div>
              <div className="text-blue-600 text-xs mt-1">
                {context === 'assessment' 
                  ? `${contextInfo.questions?.length || 0} questions currently`
                  : `${contextInfo.blocks?.length || 0} blocks currently`
                }
              </div>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search questions..."
              value={filters.searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500 text-sm"
            />
          </div>
        </div>

        {/* Quick Filters */}
        {showFilters && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question Type
                </label>
                <select
                  value={filters.questionType}
                  onChange={(e) => setFilters({ questionType: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                >
                  <option value="">All Types</option>
                  <option value="math">Mathematical</option>
                  <option value="multiple-choice">Multiple Choice</option>
                  <option value="text">Text Answer</option>
                  <option value="true-false">True/False</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Subject
                </label>
                <select
                  value={filters.subject}
                  onChange={(e) => setFilters({ subject: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-sigma-500 focus:border-sigma-500"
                >
                  <option value="">All Subjects</option>
                  <option value="mathematics">Mathematics</option>
                  <option value="physics">Physics</option>
                  <option value="chemistry">Chemistry</option>
                  <option value="biology">Biology</option>
                  <option value="geography">Geography</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Categories */}
        <div className="p-4 border-b border-gray-200">
          <div className="grid grid-cols-1 gap-2">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={clsx(
                    'flex items-center p-2 rounded-lg text-sm font-medium transition-colors',
                    selectedCategory === category.id
                      ? 'bg-sigma-100 text-sigma-700'
                      : 'text-gray-600 hover:bg-gray-100'
                  )}
                >
                  <IconComponent className="w-4 h-4 mr-2" />
                  <span>{category.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Questions List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sigma-600"></div>
            </div>
          ) : filteredQuestions.length > 0 ? (
            <div className="p-4 space-y-4">
              {filteredQuestions.map((question) => (
                <QuestionLibraryCard
                  key={question.id}
                  question={question}
                  onInsert={() => onInsertQuestion(question)}
                  context={context}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 p-4 text-center">
              <BookOpen className="w-8 h-8 text-gray-400 mb-2" />
              <p className="text-gray-500 text-sm">No questions found</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-500 text-center">
            {filteredQuestions.length} question{filteredQuestions.length !== 1 ? 's' : ''} available
          </div>
        </div>
      </div>
    </>
  );
};

export default QuestionLibrarySidebar;